import { useUnits } from '../../units/UnitContext';
import { formatUnitValue, unitLabel } from '../../units/unitConversions';
const normalize = (value) => {
  const rounded = Number(value.toFixed(8));
  return Object.is(rounded, -0) ? 0 : rounded;
};

const GaugeVisualization = ({ result }) => {
  const { unitSystem } = useUnits();
  const nominal = result.diameterMm;
  const deviation = (value) => normalize((value - nominal) * 1000);
  const formatUm = (value, signed = true) => {
    const normalized = normalize(value);
    if (unitSystem === 'inch') return `${formatUnitValue(normalized, 'um', unitSystem, { signed })} ${unitLabel('um', unitSystem)}`;
    if (normalized === 0) return '0 µm';
    const sign = signed ? (normalized > 0 ? '+' : '−') : '';
    return `${sign}${Math.abs(normalized)} µm`;

  };
  const values = {
    workpieceMin: deviation(result.workpiece.minimumMm),
    workpieceMax: deviation(result.workpiece.maximumMm),
    goDinMin: deviation(result.go.dinMinimumMm),
    goDinMax: deviation(result.go.dinMaximumMm),
    goPracticalMin: deviation(result.go.practicalMinimumMm),
    goPracticalMax: deviation(result.go.practicalMaximumMm),
    goTarget: deviation(result.go.centerMm),
    goWear: deviation(result.go.wearLimitMm),
    notGoDinMin: deviation(result.notGo.dinMinimumMm),
    notGoDinMax: deviation(result.notGo.dinMaximumMm),
    notGoPracticalMin: deviation(result.notGo.practicalMinimumMm),
    notGoPracticalMax: deviation(result.notGo.practicalMaximumMm),
    notGoTarget: deviation(result.notGo.centerMm),
  };
  const extremes = [0, ...Object.values(values)];
  const lower = Math.min(...extremes);
  const upper = Math.max(...extremes);
  const rawSpan = upper - lower || 1;
  const padding = Math.max(rawSpan * 0.1, 1);
  const plotLower = lower - padding;
  const plotUpper = upper + padding;
  const span = plotUpper - plotLower;
  const toTop = (value) => ((plotUpper - value) / span) * 100;
  const zoneStyle = (minimum, maximum) => ({
    top: `${toTop(maximum)}%`,
    height: `${Math.max(toTop(minimum) - toTop(maximum), 1.5)}%`,
  });
  const markerStyle = (value) => ({ top: `${toTop(value)}%` });
  const practicalDiffers =
    result.manufacturing.appliedHalfToleranceUm !==
    result.manufacturing.dinHalfToleranceUm;

  return (
    <section className="gaugeChart" aria-labelledby="gauge-chart-title">
      <div className="gaugeSectionHeading">
        <p>Deviation from nominal · {unitLabel('um', unitSystem)}</p>
        <h2 id="gauge-chart-title">Workpiece and gauge zones</h2>
      </div>

      <div
        className="gaugeVerticalPlot"
        role="img"
        aria-label="Vertical tolerance-zone comparison of workpiece, GO gauge, and NO-GO gauge"
      >
        <span className="gaugePlotMaximum">{formatUm(plotUpper)}</span>
        <span className="gaugePlotMinimum">{formatUm(plotLower)}</span>
        <div className="gaugeColumnHeaders">
          <strong>Workpiece · {result.designation}</strong>
          <strong>GO</strong>
          <strong>NO-GO</strong>
        </div>
        <div className="gaugeScaleArea">
          <span
            className="gaugeNominalLine"
            style={{ top: `${toTop(0)}%` }}
            data-testid="gauge-zero-line"
          />
          <span className="gaugeNominalLabel" style={{ top: `${toTop(0)}%` }}>
            Nominal · 0 {unitLabel('um', unitSystem)}
          </span>
          <div className="gaugeVerticalColumns">
            <div className="gaugeColumnPlot">
              <span
                className="gaugeVerticalZone gaugeVerticalZone--workpiece"
                style={zoneStyle(values.workpieceMin, values.workpieceMax)}
                aria-label={`Workpiece tolerance ${formatUm(values.workpieceMin)} to ${formatUm(values.workpieceMax)}`}
              />
            </div>
            <div className="gaugeColumnPlot">
              {practicalDiffers && (
                <span
                  className="gaugeVerticalZone gaugeVerticalZone--achievable"
                  style={zoneStyle(values.goPracticalMin, values.goPracticalMax)}
                  aria-label={`GO manufacturer achievable zone ${formatUm(values.goPracticalMin)} to ${formatUm(values.goPracticalMax)}`}
                />
              )}
              <span
                className="gaugeVerticalZone gaugeVerticalZone--din"
                style={zoneStyle(values.goDinMin, values.goDinMax)}
                aria-label={`GO DIN manufacturing zone ${formatUm(values.goDinMin)} to ${formatUm(values.goDinMax)}`}
              />
              <span className="gaugeHorizontalMarker gaugeHorizontalMarker--target" style={markerStyle(values.goTarget)} aria-label={`GO target ${formatUm(values.goTarget)}`} />
              <span className="gaugeHorizontalMarker gaugeHorizontalMarker--wear" style={markerStyle(values.goWear)} aria-label={`GO wear limit ${formatUm(values.goWear)}`} />
            </div>
            <div className="gaugeColumnPlot">
              {practicalDiffers && (
                <span
                  className="gaugeVerticalZone gaugeVerticalZone--achievable"
                  style={zoneStyle(values.notGoPracticalMin, values.notGoPracticalMax)}
                  aria-label={`NO-GO manufacturer achievable zone ${formatUm(values.notGoPracticalMin)} to ${formatUm(values.notGoPracticalMax)}`}
                />
              )}
              <span
                className="gaugeVerticalZone gaugeVerticalZone--din"
                style={zoneStyle(values.notGoDinMin, values.notGoDinMax)}
                aria-label={`NO-GO DIN manufacturing zone ${formatUm(values.notGoDinMin)} to ${formatUm(values.notGoDinMax)}`}
              />
              <span className="gaugeHorizontalMarker gaugeHorizontalMarker--target" style={markerStyle(values.notGoTarget)} aria-label={`NO-GO target ${formatUm(values.notGoTarget)}`} />
            </div>
          </div>
        </div>
      </div>

      <div className="gaugeColumnValues">
        <p><strong>Workpiece</strong><span>{formatUm(values.workpieceMin)} to {formatUm(values.workpieceMax)}</span></p>
        <p><strong>GO</strong><span>Target {formatUm(values.goTarget)} · Wear {formatUm(values.goWear)}</span></p>
        <p><strong>NO-GO</strong><span>Target {formatUm(values.notGoTarget)}</span></p>
      </div>

      <ul className="gaugeChartLegend">
        <li><span className="gaugeLegendWorkpiece" />Workpiece tolerance</li>
        <li><span className="gaugeLegendDin" />DIN manufacturing zone</li>
        {practicalDiffers && <li><span className="gaugeLegendPractical" />Manufacturer achievable zone</li>}
        <li><span className="gaugeLegendTarget" />Target</li>
        <li><span className="gaugeLegendWear" />Wear limit</li>
      </ul>
    </section>
  );
};

export default GaugeVisualization;
